import { ArrowUpCircle, CircleCheck, Clock, FlaskConical, HeartPulse, History, ShieldAlert, TriangleAlert, Wrench } from '@orgus/ui/icons';
import { useState } from 'react';
import type { FleetHealthReport, FleetMachine, Machine, UpdateChannel } from '@orgus/protocol';
import { CHANNEL_LABEL, FLEET_HEALTH_LABEL, compareVersions } from '@orgus/protocol';
import { Badge, Button, ErrorState, RelativeTime, Segmented, StatusDot, Tooltip, toUiError } from '@orgus/ui';
import type { FleetBadge } from './model.ts';
import { belowMinimum, holdLabel, isProblemHold, machineBadges, stageLabel, stageTone } from './model.ts';
import { fleetActions, fleetReports } from './store.ts';

const SERVICE_LABEL: Record<string, string> = {
  'task-scheduler': 'Task Scheduler',
  launchd: 'launchd',
  systemd: 'systemd user service',
  none: 'Launcher, no service',
  source: 'Source checkout',
  'in-process': 'Inside the hub',
};

/**
 * The fleet chip on a machine card: the most important badge only (a row or card carries at
 * most one chip), icon plus label, with the reason in a tooltip; the rest as "+N".
 */
export function FleetBadges({ badges }: { badges: FleetBadge[] }) {
  const [b, ...rest] = badges;
  if (!b) return null;
  return (
    <span className="flex min-w-0 items-center gap-1">
      <Tooltip content={b.title}>
        <Badge
          data-chip=""
          tabIndex={0}
          className="min-w-0 shrink"
          tone={b.tone}
          dot={b.key === 'update' && b.tone === 'working'}
          icon={b.key === 'min' ? <ShieldAlert /> : b.key === 'drift' ? <ArrowUpCircle /> : b.key === 'ring' ? <FlaskConical /> : b.tone === 'failed' ? <TriangleAlert /> : undefined}
        >
          {b.label}
        </Badge>
      </Tooltip>
      {rest.length > 0 && (
        <Tooltip content={rest.map((x) => x.label).join(', ')}>
          <span tabIndex={0} className="shrink-0 text-caption text-fg-subtle num" aria-label={`Also: ${rest.map((x) => x.label).join(', ')}`}>
            +{rest.length}
          </span>
        </Tooltip>
      )}
    </span>
  );
}

export function badgesFor(m: Machine, f: FleetMachine | null, o: { latest: string | null; minAgentVersion: string | null }): FleetBadge[] {
  return machineBadges(m, f, o);
}

const HEALTH_TONE = { healthy: 'passed', attention: 'waiting', down: 'failed' } as const;
const CHECK_TONE = { ok: 'passed', warn: 'waiting', fail: 'failed' } as const;

/** A health check's result: the verdict bright, each check small; the ones that pass stay dim. */
function HealthResult({ report: h }: { report: FleetHealthReport }) {
  return (
    <div aria-label="Health check" className="flex flex-col gap-2 border-t border-border-subtle pt-3">
      <p className="flex items-center gap-2 text-sm">
        <StatusDot tone={HEALTH_TONE[h.status]} size="sm" label="" />
        <span className="text-fg">{FLEET_HEALTH_LABEL[h.status]}</span>
        <RelativeTime ts={h.at} prefix="Checked" className="text-caption text-fg-subtle" />
      </p>
      <ul className="flex flex-col gap-1">
        {h.checks.map((c) => (
          <li key={c.key} className="flex items-start gap-2 text-caption">
            <StatusDot tone={CHECK_TONE[c.state]} size="sm" className="mt-1" label="" />
            <span className={c.state === 'ok' ? 'w-24 shrink-0 text-fg-subtle' : 'w-24 shrink-0 text-fg-muted'}>{c.label}</span>
            <span className={c.state === 'ok' ? 'min-w-0 text-fg-subtle' : 'min-w-0 text-fg-muted'}>{c.detail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Agent version, update ring and updates for one machine, in its drawer (drain is the scheduler's). */
export function MachineFleet({ machine: m, fleet: f, latest, minAgentVersion, workspaceChannel }: { machine: Machine; fleet: FleetMachine | null; latest: string | null; minAgentVersion: string | null; workspaceChannel?: UpdateChannel }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [health, setHealth] = useState<FleetHealthReport | null>(null);
  // What failed, said inline under the controls: errors never go in a toast.
  const [error, setError] = useState<unknown>(null);
  const run = (key: string, fn: () => Promise<void>, title: string) => async () => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError({ ...toUiError(e), what: title });
    } finally {
      setBusy(null);
    }
  };
  const behind = !!latest && !!m.agentVersion && compareVersions(m.agentVersion, latest) < 0;
  const below = belowMinimum(m.agentVersion, minAgentVersion);
  const updating = f?.update && !['done', 'failed', 'rolled-back'].includes(f.update.stage);

  return (
    <section aria-label="Agent and updates" className="flex flex-col gap-3">
      <h3 className="text-sm font-medium text-fg-muted">Agent and updates</h3>
      <div className="flex flex-col gap-3 rounded-md border border-border p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base text-fg num">Agent v{m.agentVersion || 'unknown'}</span>
          {below ? (
            <Badge tone="failed" icon={<ShieldAlert />}>
              Below minimum {minAgentVersion}
            </Badge>
          ) : behind ? (
            <Badge tone="waiting" icon={<ArrowUpCircle />}>
              {latest} available
            </Badge>
          ) : latest ? (
            <Badge tone="passed" icon={<CircleCheck />}>
              Latest
            </Badge>
          ) : null}
          <Tooltip content={f?.serviceEntry ? `Start entry: ${f.serviceEntry}` : 'How the agent is kept running'}>
            <span tabIndex={0} className="ml-auto text-caption text-fg-subtle">
              {f?.service ? SERVICE_LABEL[f.service] ?? f.service : 'Not reported yet'}
            </span>
          </Tooltip>
        </div>
        {below && <p className="text-sm text-fg-muted">Write actions to {m.name} are refused until its agent reaches {minAgentVersion}. Reads keep working.</p>}

        {f?.update && (
          <div className="flex items-start gap-2 text-sm">
            <StatusDot tone={stageTone(f.update.stage)} size="sm" className="mt-1.5" label="" />
            <p className="min-w-0 flex-1 text-fg-muted">
              <span className="text-fg">
                {stageLabel(f.update.stage)} {f.update.stage === 'rolled-back' ? 'from' : 'to'} {f.update.version}
              </span>
              {f.update.message ? `: ${f.update.message}` : ''} <RelativeTime ts={f.update.at} className="text-fg-subtle" />
            </p>
          </div>
        )}
        {f?.rolledBackFrom && (
          <p className="flex items-start gap-2 text-sm text-fg-muted">
            <History className="mt-0.5 size-3.5 shrink-0 text-waiting" aria-hidden />
            Agent {f.rolledBackFrom} did not start here, so the launcher went back to {m.agentVersion}.
          </p>
        )}
        {f?.hold && f.updatable !== false && (
          <p className="flex items-start gap-2 text-sm text-fg-muted">
            {isProblemHold(f.hold) ? <TriangleAlert className="mt-1 size-3.5 shrink-0 text-waiting" aria-hidden /> : <Clock className="mt-1 size-3.5 shrink-0 text-fg-subtle" aria-hidden />}
            <span className="min-w-0">
              <span className="text-fg">Not updating yet: {holdLabel(f.hold).toLowerCase()}.</span> {f.hold.detail}
              {f.hold.until ? (
                <>
                  {' '}
                  <RelativeTime ts={f.hold.until} prefix="Ends" className="text-fg-subtle" />
                </>
              ) : null}
            </span>
          </p>
        )}
        {f && f.updatable === false && (
          <p className="flex items-start gap-2 text-sm text-fg-muted">
            <Wrench className="mt-0.5 size-3.5 shrink-0 text-fg-subtle" aria-hidden />
            Updates by hand: {f.why}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 border-t border-border-subtle pt-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-fg-muted">Update ring</span>
            <Segmented
              label="Update ring"
              value={f?.ring ?? 'fleet'}
              onChange={(ring) => void run('ring', () => fleetActions.setRing(m.id, ring), 'The ring was not changed')()}
              options={[
                { value: 'test', label: 'Test ring', icon: <FlaskConical /> },
                { value: 'fleet', label: 'Fleet' },
              ]}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-fg-muted">Channel</span>
            <Segmented<'workspace' | UpdateChannel>
              label="Update channel"
              value={f?.channelOverride ?? 'workspace'}
              onChange={(c) => void run('channel', () => fleetActions.setChannel(m.id, c === 'workspace' ? null : c), 'The channel was not changed')()}
              options={[
                { value: 'workspace', label: `Default (${CHANNEL_LABEL[workspaceChannel ?? 'stable'].toLowerCase()})` },
                { value: 'stable', label: 'Stable' },
                { value: 'beta', label: 'Beta' },
              ]}
            />
          </div>
          <Button
            size="sm"
            variant="ghost"
            icon={<HeartPulse />}
            className="ml-auto"
            disabled={busy === 'health'}
            onClick={run('health', async () => setHealth(await fleetReports.checkHealth(m.id)), 'Health was not checked')}
          >
            Check health
          </Button>
          {(behind || below) && latest && f?.updatable !== false && (
            <Button
              size="sm"
              icon={<ArrowUpCircle />}
              disabled={!m.online || !!updating || busy === 'update'}
              onClick={run('update', () => fleetActions.updateMachine(m.id), `${m.name} was not updated`)}
            >
              {m.online ? `Update to ${latest}` : 'Update when online'}
            </Button>
          )}
        </div>
        {health?.machineId === m.id && <HealthResult report={health} />}
        {error != null && <ErrorState variant="inline" error={error} />}
      </div>
    </section>
  );
}
